import { CustomAttribute, CustomAttributeDefinition, Member, MemberRole, MemberStatus } from '@quick/client';
import { sentenceCase } from 'sentence-case';

/**
 * Show active members first, then pending ones, then others (inactive, etc)
 * With in each group, sort by communityId
 * @param members
 */
export const sortMembers = (members: Member[]): Member[] => {
  const activeGroup = members
    .filter((m) => m.status === MemberStatus.Active)
    .sort((a, b) => a.communityId - b.communityId);
  const pendingGroup = members
    .filter((m) => m.status === MemberStatus.PendingApproval)
    .sort((a, b) => a.communityId - b.communityId);
  const otherGroup = members
    .filter((m) => m.status !== MemberStatus.PendingApproval && m.status !== MemberStatus.Active)
    .sort((a, b) => a.communityId - b.communityId);
  return [...activeGroup, ...pendingGroup, ...otherGroup];
};

export const shouldUseManagementApi = (member: Member) => {
  return member.role === MemberRole.Manager || member.role === MemberRole.BoardMember;
};

export const getCustomAttribute = (customAttributes: CustomAttribute[], definition: CustomAttributeDefinition) => {
  return customAttributes.find((attribute) => attribute.name === definition.name);
};

export const isCommunityAdmin = (member: Member) => {
  return member.role === MemberRole.Manager || member.role === MemberRole.BoardMember;
};

export const memberDescription = (member: Member): string | undefined => {
  if (isCommunityAdmin(member)) {
    return memberDisplayRole(member);
  }
  return member.property?.address ?? memberDisplayRole(member);
};

export const memberDisplayRole = (member: Member): string => {
  switch (member.role) {
    case MemberRole.Manager:
      return 'Property manager';
    default:
      return sentenceCase(member.role);
  }
};

export const guessMemberFirstName = (member: Member | undefined): string | undefined => {
  if (!member) {
    return undefined;
  }
  const words = member.name
    .split(' ')
    .map((it) => it.trim())
    .filter((w) => w);
  if (words.length < 1) {
    return undefined;
  }
  return words[0];
};

export const guessMemberLastName = (member: Member | undefined): string | undefined => {
  if (!member) {
    return undefined;
  }
  const words = member.name
    .split(' ')
    .map((it) => it.trim())
    .filter((w) => w);
  if (words.length < 2) {
    return undefined;
  }
  return words.slice(1).join(' ');
};
